import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { usePermissionRedirect } from "@/hooks/usePermissionRedirect";
import { sanitizeText } from "@/lib/sanitize";
import { getCurrentUser } from "@/lib/authUser";

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGroupCreated: (groupId: string) => void;
}

export function CreateGroupDialog({
  open,
  onOpenChange,
  onGroupCreated,
}: CreateGroupDialogProps) {
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const { checkGroupAccess, checkGroupLimit } = usePermissionRedirect();

  const handleCreate = async () => {
    // Check permissions before proceeding
    if (!checkGroupAccess()) return;
    if (!(await checkGroupLimit())) return;
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    setLoading(true);
    try {
      const user = await getCurrentUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // Create group
      const sanitizedName = sanitizeText(groupName, 100);
      const { data: group, error: groupError } = await supabase
        .from("groups")
        .insert({ name: sanitizedName, created_by: user.id })
        .select()
        .single();

      if (groupError) throw groupError;

      // Add creator as leader
      const { error: memberError } = await supabase
        .from("group_members")
        .insert({ group_id: group.id, user_id: user.id, role: "leader" });

      if (memberError) throw memberError;

      toast.success("Group created successfully");
      setGroupName("");
      onOpenChange(false);
      onGroupCreated(group.id);
    } catch (error: any) {
      console.error("Error creating group:", error);
      toast.error(error.message || "Failed to create group");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Group</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="group-name">Group Name</Label>
            <Input
              id="group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
